/**
 * question-model — the field config the question editor adds on top of the
 * stored `Question` shape.
 *
 * Promoted out of `sandbox/` on 2026-07-31 (Pat): the sandbox editor IS the
 * question editor now. The field-type CATALOG lives in `./useQuestions`
 * (single source — the library, the group editor, surveys and the event-day
 * renderer all read it); this module owns only the per-type configuration that
 * the editor writes.
 */
import { type Question } from "./useQuestions";
import type { QTextRules } from "./text-validation";

/** One −/+ row. A stepper question has one row, or several when `allowMultiple`. */
export interface QStepperRow {
  id: string;
  label: string;
  helper: string;
  min: number | null;
  max: number | null;
  /** Increment size for the −/+ buttons. Omitted → the control's default of 1. */
  step?: number;
  defaultValue: number | null;
}

export interface QStepperConfig {
  /** Several named rows in one question (Parents / Kids / …). */
  allowMultiple: boolean;
  rows: QStepperRow[];
}

/**
 * `Question` plus the fields only the editor writes. Intersection rather than a
 * second interface so it stays assignable to `Question` for the shared store.
 */
export type EditorQuestion = Question & {
  /** Short/long text: the greyed hint inside the empty field. */
  placeholder?: string;
  /** Short/long text: value the field starts with (prefill). */
  defaultValue?: string;
  hiddenInBooking?: boolean;
  stepper?: QStepperConfig;
  yesno?: QYesNoConfig;
  file?: QFileConfig;
  select?: QSelectConfig;
  info?: QInfoConfig;
  textRules?: QTextRules;
};

/* ── Info text ──────────────────────────────────────────────────── */

/**
 * Info text can read as a coloured banner rather than a paragraph. `plain` stays
 * the default so existing info-text questions render exactly as before — a
 * silent restyle of live content would be the wrong kind of surprise.
 */
export const Q_INFO_TONES = [
  { id: "plain",   labelKey: "questions.info.tone.plain",   label: "Plain text" },
  { id: "info",    labelKey: "questions.info.tone.info",    label: "Info" },
  { id: "success", labelKey: "questions.info.tone.success", label: "Success" },
  { id: "warning", labelKey: "questions.info.tone.warning", label: "Warning" },
  { id: "error",   labelKey: "questions.info.tone.error",   label: "Error" },
] as const;

export type QInfoToneId = (typeof Q_INFO_TONES)[number]["id"];

export interface QInfoConfig {
  tone: QInfoToneId;
  /** Optional bold lead-in above the body, e.g. "Please note". */
  title?: string;
}

export const emptyInfoConfig = (): QInfoConfig => ({ tone: "plain" });

/* ── Yes / no ───────────────────────────────────────────────────── */

/**
 * A yes/no question is a SWITCH, not a pair of buttons (operator, 2026-07-31):
 * it is one thing that is either on or off, which is what a toggle says and
 * what two equal-weight buttons do not.
 *
 * `startsAs: "unset"` is the default and exists so Required keeps meaning
 * something. A plain boolean toggle always carries a value, so every yes/no
 * question would silently satisfy any Required rule — with `unset` the switch
 * opens in a neutral state and the answer is genuinely absent until touched.
 */
export type QYesNoStart = "unset" | "no" | "yes";

export interface QYesNoConfig {
  startsAs: QYesNoStart;
  /** Shown, and submitted, but the customer cannot change it. */
  readOnly: boolean;
}

export const emptyYesNoConfig = (): QYesNoConfig => ({ startsAs: "unset", readOnly: false });

/* ── Dropdown ──────────────────────────────────────────────────── */

/**
 * The former "Multi-select" type is GONE as a type (operator, 2026-07-30): one
 * Dropdown with a multiple-selection toggle instead. Two field types that
 * differed only in cardinality meant authors had to pick the right one up front
 * and re-create the question to change their mind.
 */
export interface QSelectConfig {
  allowMultiple: boolean;
  sortAlphabetically: boolean;
}

export const emptySelectConfig = (): QSelectConfig => ({ allowMultiple: false, sortAlphabetically: false });

/** Options in display order — author order, or A–Z when the author asked for it. */
export function displayOptions<TOpt extends { label: string }>(opts: TOpt[], cfg: QSelectConfig | undefined): TOpt[] {
  if (!cfg?.sortAlphabetically) return opts;
  // localeCompare so Ä/é/ø sort where a reader expects, not by code point.
  return [...opts].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Read-side migration for records authored before the merge:
 *  · a stored `multi` question becomes a Dropdown with multiple selection on;
 *  · `textRules.hiddenInBooking` moves up to the question, where Hidden now
 *    applies to every type rather than to text only.
 *
 * ⚠️ Saving a migrated question REWRITES its stored shape in the shared library.
 * That is the intended end state of the merge, but it is a real data change, not
 * a display trick. Flagged in the changelog and in chat.
 */
export function normalizeQuestion(q: EditorQuestion): EditorQuestion {
  let out = q;
  const legacy = q.textRules as (QTextRules & { hiddenInBooking?: boolean }) | undefined;
  if (legacy?.hiddenInBooking && !q.hiddenInBooking) {
    const { hiddenInBooking: _moved, ...rest } = legacy;
    out = { ...out, hiddenInBooking: true, textRules: rest };
  }
  if (out.type !== "multi") return out;
  return { ...out, type: "select", select: { allowMultiple: true, sortAlphabetically: out.select?.sortAlphabetically ?? false } };
}

/* ── File upload ─────────────────────────────────────────────────── */

/**
 * Accepted formats are per-FORMAT, not two coarse groups, so an author can ask
 * for "PDF only" — which is the common real case. The groups are presentation.
 */
// ui-config — file-format names (PNG, JPEG, GIF, PDF, Excel): format tokens, never translated
export const Q_FILE_FORMATS = [
  { id: "png",   label: "PNG",   group: "images",    accept: "image/png,.png" },
  { id: "jpeg",  label: "JPEG",  group: "images",    accept: "image/jpeg,.jpg,.jpeg" },
  { id: "gif",   label: "GIF",   group: "images",    accept: "image/gif,.gif" },
  { id: "pdf",   label: "PDF",   group: "documents", accept: "application/pdf,.pdf" },
  { id: "excel", label: "Excel", group: "documents", accept: "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx" },
] as const;

export type QFileFormatId = (typeof Q_FILE_FORMATS)[number]["id"];

/**
 * Size tiers. ⚠️ The MB figures are CD-chosen placeholders — the real ceiling is
 * whatever the upload service enforces, which the contract does not expose.
 * Tracked in REQ-109; the labels show the number so nobody has to guess, and the
 * tier id is what gets stored so a server-side change of numbers needs no data
 * migration.
 */
export const Q_FILE_SIZES = [
  { id: "small",  labelKey: "questions.file.size.small",  label: "Small",  mb: 2 },
  { id: "medium", labelKey: "questions.file.size.medium", label: "Medium", mb: 10 },
  { id: "large",  labelKey: "questions.file.size.large",  label: "Large",  mb: 25 },
] as const;

export type QFileSizeId = (typeof Q_FILE_SIZES)[number]["id"];

export interface QFileConfig {
  formats: QFileFormatId[];
  allowMultiple: boolean;
  maxSize: QFileSizeId;
}

export const emptyFileConfig = (): QFileConfig => ({ formats: ["pdf"], allowMultiple: false, maxSize: "medium" });

export const fileSize = (id: QFileSizeId | undefined) => Q_FILE_SIZES.find((s) => s.id === id) ?? Q_FILE_SIZES[1];

/** `accept` attribute for the preview input — empty string means "any file". */
export function fileAccept(cfg: QFileConfig | undefined): string {
  const ids = cfg?.formats ?? [];
  if (!ids.length) return "";
  return Q_FILE_FORMATS.filter((f) => ids.includes(f.id)).map((f) => f.accept).join(",");
}

let _rowSeq = 0;
export const newStepperRow = (label = ""): QStepperRow => ({
  id: `sr_${Date.now().toString(36)}${(_rowSeq++).toString(36)}`,
  label,
  helper: "",
  min: 0,
  max: 10,
  defaultValue: 0,
});

export const emptyStepper = (): QStepperConfig => ({ allowMultiple: false, rows: [newStepperRow("")] });

export const isTextType = (id: string) => id === "short" || id === "long";

/** Clamp a stepper value into its row's range. */
export function clampStep(row: QStepperRow, n: number): number {
  const lo = typeof row.min === "number" ? row.min : -Infinity;
  const hi = typeof row.max === "number" ? row.max : Infinity;
  return Math.min(hi, Math.max(lo, n));
}

/** Rows a stepper actually renders — one when multiple is off. */
export function stepperRows(cfg: QStepperConfig | undefined): QStepperRow[] {
  const rows = cfg?.rows?.length ? cfg.rows : [newStepperRow("")];
  return cfg?.allowMultiple ? rows : rows.slice(0, 1);
}
